import {
  createContext, createElement, useCallback, useContext, useEffect, useMemo, useState,
  type ReactNode,
} from "react";

/**
 * Appearance, the way macOS handles it: the app follows the system setting
 * until the user overrides it here, and keeps following live changes while it
 * has no override. Only an explicit choice is persisted, so clearing the
 * stored value hands control back to the system.
 *
 * This lives above the router so /login and /invite are themed too — they
 * render outside the Shell, and previously got no appearance at all.
 */
const KEY = "theme";
const DARK_QUERY = "(prefers-color-scheme: dark)";

function storedChoice(): "light" | "dark" | null {
  const v = localStorage.getItem(KEY);
  return v === "light" || v === "dark" ? v : null;
}

const AppearanceContext = createContext<{
  dark: boolean;
  followsSystem: boolean;
  toggle: () => void;
  followSystem: () => void;
} | null>(null);

export function AppearanceProvider({ children }: { children: ReactNode }) {
  const [choice, setChoice] = useState<"light" | "dark" | null>(storedChoice);
  const [systemDark, setSystemDark] = useState(
    () => window.matchMedia(DARK_QUERY).matches,
  );

  useEffect(() => {
    const mq = window.matchMedia(DARK_QUERY);
    const onChange = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const dark = choice === null ? systemDark : choice === "dark";

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
  }, [dark]);

  const toggle = useCallback(() => {
    setChoice((prev) => {
      const next = (prev === null ? systemDark : prev === "dark") ? "light" : "dark";
      localStorage.setItem(KEY, next);
      return next;
    });
  }, [systemDark]);

  const followSystem = useCallback(() => {
    localStorage.removeItem(KEY);
    setChoice(null);
  }, []);

  const value = useMemo(
    () => ({ dark, followsSystem: choice === null, toggle, followSystem }),
    [dark, choice, toggle, followSystem],
  );
  return createElement(AppearanceContext.Provider, { value }, children);
}

export function useAppearance() {
  const ctx = useContext(AppearanceContext);
  if (!ctx) throw new Error("useAppearance must be used inside AppearanceProvider");
  return ctx;
}
